/* eslint-disable unicorn/filename-case */
import { useForm } from '@tanstack/react-form';
import {
  HelpCircle,
  LayersThree02,
  RefreshCcw01,
  Rocket02,
  Stars01,
} from '@untitled-ui/icons-react';
// import type { TFunctionNonStrict } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
// import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { BlogGenerationCard } from '../../../components/blog-generation-card';
import { ButtonSeeHowItWorks } from '../../../components/button-see-how-it-works';
import { Button } from '../../../components/buttons/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/dialog';
import { Divider } from '../../../components/divider';
import { GoogleIcon } from '../../../components/icons/google-icon';
// import { TextArea } from '../../../components/inputs/text-area';
import { TextField } from '../../../components/inputs/text-field';
import { Loading } from '../../../components/loading';
import { LightTooltip } from '../../../components/onboarding-tooltip';
import { SelectField } from '../../../components/select-field';
import { Tooltip } from '../../../components/tooltip';
import { getConfig } from '../../../config';
import {
  type GeneratePrimaryKeywordSocketRes,
  // type GeneratePrimaryPromptSocketRes,
} from '../../../entity/api/routes/article';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
// import { generateKeyword, generatePrimaryPrompt } from '../../../sdk/article';
import { generateKeyword } from '../../../sdk/article';
import { TooltipId, useTooltipStore } from '../../../services/storage/tooltips';
import { tGlobal, tNamespace } from '../../../services/translation';
import { countriesOptions, getLanguages } from '../../../shared/countries';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { usePostHogSessionRecording } from '../../../shared/hooks/use-posthog-session-recording';
import { isMobile } from '../../../shared/mobile';
import { parseFirstZodError } from '../../../shared/validation';
import {
  type KeywordSuggestion,
  KeywordSuggestionsDropdown,
} from './-keyword-suggestions-dropdown';

const t = tNamespace('app.blogs.-section-modal-generation');

type ModalGenerationProps = {
  brandId: string;
  hasZeroArticles?: boolean;
  isOpen: boolean;
  keyword: string;
  topic: string;
  country: string;
  language: string;
  onOpenChange: (open: boolean) => void;
  createContent: (
    keyword: string,
    prompt: string,
    language: string,
    country: string,
    topic?: string | null
  ) => Promise<void>;
  generateOneClick: (
    topic: string,
    keyword: string,
    prompt: string,
    language: string,
    country: string
  ) => Promise<void>;
  isLoading: boolean;
  shouldShowTooltips?: boolean;
};

enum GenerationType {
  Funnel = 'funnel',
  OneClick = 'one-click',
}

enum KeywordGenerationStatus {
  NotStarted = 'not-started',
  Generating = 'generating',
  Generated = 'generated',
  Error = 'error',
}

enum PromptGenerationStatus {
  NotStarted = 'not-started',
  Generating = 'generating',
  Generated = 'generated',
  Error = 'error',
}

/**
 * Helper function to check if a field is valid and has a value
 */
function isFieldValid(fieldMeta: any, value: any, checkNull = false): boolean {
  if (fieldMeta == null) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const hasNoErrors = Array.isArray(fieldMeta.errors) && fieldMeta.errors.length === 0;
  if (checkNull) {
    return hasNoErrors && value != null;
  }
  return hasNoErrors && typeof value === 'string' && value.trim().length > 0;
}

const icon = {
  [KeywordGenerationStatus.NotStarted]: <Stars01 width={16} height={16} />,
  [KeywordGenerationStatus.Generating]: <Loading size="xs" />,
  [KeywordGenerationStatus.Generated]: <RefreshCcw01 width={16} height={16} />,
  [KeywordGenerationStatus.Error]: <RefreshCcw01 width={16} height={16} />,
};
function getKeywordLabel(): Record<KeywordGenerationStatus, string> {
  return {
    [KeywordGenerationStatus.NotStarted]: t('generateKeyword'),
    [KeywordGenerationStatus.Generating]: t('generating'),
    [KeywordGenerationStatus.Generated]: t('generated'),
    [KeywordGenerationStatus.Error]: t('error'),
  };
}

function getPromptLabel(): Record<PromptGenerationStatus, string> {
  return {
    [PromptGenerationStatus.NotStarted]: t('generatePrompt'),
    [PromptGenerationStatus.Generating]: t('generating'),
    [PromptGenerationStatus.Generated]: t('generated'),
    [PromptGenerationStatus.Error]: t('error'),
  };
}

export function ModalGeneration(props: ModalGenerationProps) {
  const {
    hasZeroArticles = false,
    isOpen,
    onOpenChange,
    keyword,
    topic,
    country,
    language,
    createContent,
    isLoading,
    brandId,
    generateOneClick,
    shouldShowTooltips = false,
  } = props;
  const { defaultCountry, defaultLanguage } = getConfig();
  const isMobileDevice = isMobile();
  const shouldShowTooltipsActive = shouldShowTooltips && !isMobileDevice;
  const [keywordGenerationStatus, setKeywordGenerationStatus] = useState<KeywordGenerationStatus>(
    KeywordGenerationStatus.NotStarted
  );
  // const [promptGenerationStatus, setPromptGenerationStatus] = useState<PromptGenerationStatus>(
  //   PromptGenerationStatus.NotStarted
  // );
  // const [promptGenerationError, setPromptGenerationError] = useState<string | null>(null);
  const [isInternalLoading, setIsInternalLoading] = useState(false);
  const [keywordGenerationError, setKeywordGenerationError] = useState<string | null>(null);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState<KeywordSuggestion[]>([]);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const keywordInputFunnelRef = useRef<HTMLInputElement | null>(null);
  const keywordInputOneClickRef = useRef<HTMLInputElement | null>(null);
  const [showTopicTooltip, setShowTopicTooltip] = useState(false);
  const [showKeywordTooltip, setShowKeywordTooltip] = useState(false);
  const hasShownTopicTooltipThisOpenRef = useRef(false);
  const hasShownKeywordTooltipThisOpenRef = useRef(false);

  // Zustand store for tooltip persistence
  const shouldShowTopicTooltip = useTooltipStore(function (state) {
    return state.shouldShowTooltip(TooltipId.MODAL_GENERATION_TOPIC);
  });
  const shouldShowKeywordTooltip = useTooltipStore(function (state) {
    return state.shouldShowTooltip(TooltipId.MODAL_GENERATION_KEYWORD);
  });
  const dismissTooltip = useTooltipStore(function (state) {
    return state.dismissTooltip;
  });

  const checkScrollPosition = useCallback(function () {
    const container = scrollContainerRef.current;
    if (container == null) {
      setShowFooterShadow(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

    // Show shadow if content is scrollable AND not at the bottom
    setShowFooterShadow(isScrollable && !isAtBottom);
  }, []);

  // Callback ref that fires when the element is mounted/unmounted
  const scrollContainerCallbackRef = useCallback(
    function (node: HTMLDivElement | null) {
      // Cleanup previous listeners
      if (scrollContainerRef.current != null) {
        scrollContainerRef.current.removeEventListener('scroll', checkScrollPosition);
      }
      if (resizeObserverRef.current != null) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      // Store the new ref
      scrollContainerRef.current = node;

      // Setup new listeners if node exists
      if (node != null) {
        // Check initial position
        checkScrollPosition();

        // Add scroll listener
        node.addEventListener('scroll', checkScrollPosition);

        // Create ResizeObserver to detect content size changes
        resizeObserverRef.current = new ResizeObserver(checkScrollPosition);
        resizeObserverRef.current.observe(node);
      }
    },
    [checkScrollPosition]
  );

  // Cleanup on unmount
  useEffect(
    function () {
      return function () {
        if (scrollContainerRef.current != null) {
          scrollContainerRef.current.removeEventListener('scroll', checkScrollPosition);
        }
        if (resizeObserverRef.current != null) {
          resizeObserverRef.current.disconnect();
        }
      };
    },
    [checkScrollPosition]
  );

  // Reset tooltip visibility and "shown this open" guards on each modal open/close
  useEffect(
    function () {
      hasShownTopicTooltipThisOpenRef.current = false;
      hasShownKeywordTooltipThisOpenRef.current = false;
      if (!isOpen) {
        setShowTopicTooltip(false);
        setShowKeywordTooltip(false);
      }
    },
    [isOpen]
  );

  usePostHogSessionRecording(isOpen);

  const form = useForm({
    defaultValues: {
      topic,
      keyword,
      prompt: '',
      country: country.toLowerCase() ?? defaultCountry,
      language: language.toLowerCase() ?? defaultLanguage,
      generationType:
        hasZeroArticles || isMobileDevice
          ? GenerationType.OneClick
          : (GenerationType.Funnel as GenerationType | null),
    },
    onSubmit: function (data) {
      // do not go through if already in there
      if (isInternalLoading) {
        return;
      }

      setIsInternalLoading(true);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async function () {
        if (data.value.generationType === GenerationType.OneClick) {
          return generateOneClick(
            data.value.topic,
            data.value.keyword,
            data.value.keyword ?? '', // TODO: Reset this to primary prompt again
            data.value.language,
            data.value.country
          );
        }

        return createContent(
          data.value.keyword,
          data.value.keyword ?? '', // TODO: Reset this to primary promp again
          data.value.language,
          data.value.country,
          data.value.topic
        );
      })().finally(function () {
        setIsInternalLoading(false);
      });
    },
  });

  function generatePrimaryKeyword() {
    if (keywordGenerationStatus === KeywordGenerationStatus.Generating) {
      return;
    }
    setShowKeywordSuggestions(false);
    setKeywordGenerationStatus(KeywordGenerationStatus.Generating);
    setKeywordGenerationError(null);

    let generationPromise: Promise<GeneratePrimaryKeywordSocketRes> | null = null;
    try {
      generationPromise = generateKeyword({
        brandId: brandId,
        topic: form.state.values.topic,
        previousKeywords: form.state.values.keyword,
        language: form.state.values.language,
        country: form.state.values.country,
      });
    } catch (err) {
      captureException(
        err,
        {
          file: 'app/blogs/-section-modal-generation.tsx',
          method: 'generatePrimaryKeyword',
          infoMessage: 'unable to create generation promise',
        },
        trackError
      );
      setKeywordGenerationStatus(KeywordGenerationStatus.Error);
      setKeywordGenerationError('UNABLE_TO_GENERATE_KEYWORD');
    }

    // Handle the promise resolution/rejection when the socket event arrives
    generationPromise
      ?.then(function (response) {
        setKeywordGenerationStatus(KeywordGenerationStatus.Generated);
        const { result } = response;
        if (result != null && 'primaryKeywordChoices' in result) {
          const { primaryKeywordChoices } = result;
          const sortedPrimaryKeywordChoices = primaryKeywordChoices.sort(function (a, b) {
            return (b.volume ?? 0) - (a.volume ?? 0);
          });
          if (sortedPrimaryKeywordChoices.length > 0) {
            setKeywordSuggestions(sortedPrimaryKeywordChoices);
            setShowKeywordSuggestions(true);
          }
        }

        if (result != null && 'generatedPrimaryKeyword' in result) {
          form.setFieldValue('keyword', result.generatedPrimaryKeyword);
          void form.validateField('keyword', 'change');
        }
      })
      .catch(function (err) {
        setKeywordGenerationStatus(KeywordGenerationStatus.Error);
        setKeywordGenerationError('UNABLE_TO_GENERATE_KEYWORD');
        captureException(
          err,
          {
            file: 'app/blogs/-section-modal-generation.tsx',
            method: 'generatePrimaryKeyword',
            infoMessage: 'unable to generate primary keyword',
          },
          trackError
        );
      });
  }

  // function generatePrompt() {
  //   if (promptGenerationStatus === PromptGenerationStatus.Generating) {
  //     return;
  //   }
  //   setPromptGenerationStatus(PromptGenerationStatus.Generating);
  //   setPromptGenerationError(null);

  //   let generationPromise: Promise<GeneratePrimaryPromptSocketRes> | null = null;
  //   try {
  //     generationPromise = generatePrimaryPrompt({
  //       brandId: brandId,
  //       topic: form.state.values.topic,
  //       primaryKeyword: form.state.values.keyword,
  //       language: form.state.values.language,
  //       country: form.state.values.country,
  //     });
  //   } catch (err) {
  //     captureException(
  //       err,
  //       {
  //         file: 'app/blogs/-section-modal-generation.tsx',
  //         method: 'generatePrimaryPrompt',
  //         infoMessage: 'unable to create prompt generation promise',
  //       },
  //       trackError
  //     );
  //     setPromptGenerationStatus(PromptGenerationStatus.Error);
  //     setPromptGenerationError('UNABLE_TO_GENERATE_PROMPT');
  //   }

  //   // Handle the promise resolution/rejection when the socket event arrives
  //   generationPromise
  //     ?.then(function (response) {
  //       setPromptGenerationStatus(PromptGenerationStatus.Generated);
  //       // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  //       const { result } = response;

  //       if (result != null) {
  //         form.setFieldValue('prompt', result.generatedPrimaryPrompt);
  //         void form.validateField('prompt', 'change');
  //       }
  //     })
  //     .catch(function (err) {
  //       setPromptGenerationStatus(PromptGenerationStatus.Error);
  //       setPromptGenerationError('UNABLE_TO_GENERATE_PROMPT');
  //       captureException(
  //         err,
  //         {
  //           file: 'app/blogs/-section-modal-generation.tsx',
  //           method: 'generatePrimaryPrompt',
  //           infoMessage: 'unable to generate primary prompt',
  //         },
  //         trackError
  //       );
  //     });
  // }

  function ButtonKeywordGeneration(props: {
    generationType: KeywordGenerationStatus | PromptGenerationStatus;
    isDisabled: boolean;
    className?: string;
    generationAction: () => void;
    isPrompt?: boolean;
  }) {
    const className = twMerge(
      props.isDisabled
        ? 'w-full whitespace-nowrap text-branding-gray-200 cursor-not-allowed hover:bg-transparent'
        : 'w-full whitespace-nowrap hover:cursor-pointer',
      props.className
    );
    const label = props.isPrompt
      ? getPromptLabel()[props.generationType as PromptGenerationStatus]
      : getKeywordLabel()[props.generationType as KeywordGenerationStatus];

    if (
      props.generationType === KeywordGenerationStatus.Generating ||
      props.generationType === PromptGenerationStatus.Generating
    ) {
      return (
        <div className={'mr-2 flex items-center gap-2 text-branding-accent-700'}>
          {icon[props.generationType]}
          <p className="font-body-2-sb text-branding-accent-700">{label}</p>
        </div>
      );
    }
    return (
      <Button
        size="sm"
        variant={
          props.generationType === KeywordGenerationStatus.Generated ||
          !isFeatureAvailable(Feature.GEO_INFORMATION)
            ? 'ghost-accent'
            : 'gradient-premium'
        }
        className={className}
        leftIcon={icon[props.generationType]}
        onClick={function () {
          if (props.isDisabled) {
            return;
          }
          props.generationAction();
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        hasCloseButton={true}
        fixedHeight
        aria-describedby={undefined}
        className="w-full max-w-[600px] px-0"
        onOpenAutoFocus={function (e) {
          if (isMobileDevice) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle align="center">{t('title')}</DialogTitle>
          <div className="flex items-center justify-center">
            <ButtonSeeHowItWorks videoUrl="https://www.youtube.com/embed/s1uq4uh4KV4" />
          </div>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={function (e) {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit().catch(function (err) {
              captureException(
                err,
                {
                  file: 'app/blogs/-section-modal-generation.tsx',
                  method: 'onSubmit',
                  infoMessage: 'unable to create content',
                },
                trackError
              );
            });
          }}
        >
          <DialogBody ref={scrollContainerCallbackRef} className="mt-4 px-4 md:mt-8 md:px-6">
            <div className="flex flex-col gap-4">
              <div>
                <form.Field
                  name="topic"
                  // eslint-disable-next-line react/no-children-prop
                  children={function (field) {
                    return (
                      <TextField
                        autoFocus
                        name={field.name}
                        sizeField="md"
                        data-onboarding-id="modal-generation-topic"
                        label={
                          <>
                            {t('contentTopic')}{' '}
                            <span className="text-branding-gray">{t('optional')}</span>
                          </>
                        }
                        placeholder={t('topicPlaceholder')}
                        value={field.state.value}
                        onChange={function (e) {
                          field.handleChange(e.target.value);
                        }}
                        onFocus={function () {
                          if (
                            shouldShowTooltipsActive &&
                            shouldShowTopicTooltip &&
                            !hasShownTopicTooltipThisOpenRef.current
                          ) {
                            setShowTopicTooltip(true);
                            hasShownTopicTooltipThisOpenRef.current = true;
                          }
                        }}
                        onBlur={function () {
                          setShowTopicTooltip(false);
                        }}
                        error={field.state.meta?.errors}
                        autoComplete="off"
                      />
                    );
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex w-full gap-4">
                  <div className="w-1/2">
                    <form.Field
                      name="country"
                      validators={{
                        onChange: function (data) {
                          return parseFirstZodError(
                            z.string().min(1, 'COUNTRY_REQUIRED'),
                            data.value
                          );
                        },
                      }}
                      // eslint-disable-next-line react/no-children-prop
                      children={function (field) {
                        return (
                          <SelectField
                            value={field.state.value}
                            placeholder={tGlobal('selectACountry')}
                            onChange={field.handleChange}
                            options={countriesOptions}
                            size="md"
                            contentFit={true}
                            className="w-full"
                            triggerClassName="w-full"
                            contentClassName="z-[100]"
                            label={tGlobal('country')}
                            tooltip={
                              <Tooltip
                                text={tGlobal('countryTooltip')}
                                description={tGlobal('descCountryTooltip')}
                              >
                                <div>
                                  <HelpCircle width={12} height={12} />
                                </div>
                              </Tooltip>
                            }
                            error={field.state.meta?.errors}
                          />
                        );
                      }}
                    />
                  </div>
                  <div className="w-1/2">
                    <form.Field
                      name="language"
                      validators={{
                        onChange: function (data) {
                          return parseFirstZodError(
                            z.string().min(1, 'LANGUAGE_REQUIRED'),
                            data.value
                          );
                        },
                      }}
                      // eslint-disable-next-line react/no-children-prop
                      children={function (field) {
                        return (
                          <SelectField
                            value={field.state.value}
                            placeholder={tGlobal('selectALanguage')}
                            onChange={field.handleChange}
                            options={getLanguages()}
                            size="md"
                            contentFit={true}
                            className="w-full"
                            triggerClassName="w-full"
                            contentClassName="z-[100]"
                            label={tGlobal('language')}
                            tooltip={
                              <Tooltip
                                text={tGlobal('languageTooltip')}
                                description={tGlobal('descLanguageTooltip')}
                              >
                                <div>
                                  <HelpCircle width={12} height={12} />
                                </div>
                              </Tooltip>
                            }
                            error={field.state.meta?.errors}
                          />
                        );
                      }}
                    />
                  </div>
                </div>
              </div>

              {isFeatureAvailable(Feature.GEO_INFORMATION) ? (
                <div className="flex flex-col gap-4 rounded-md bg-gradient-to-r from-branding-accent-50 to-lavander-100 p-4">
                  <div className="relative" ref={keywordInputFunnelRef}>
                    <div>
                      <form.Field
                        name="keyword"
                        validators={{
                          onChange: function (data) {
                            return parseFirstZodError(
                              z.string().min(1, 'KEYWORD_REQUIRED'),
                              data.value
                            );
                          },
                        }}
                        // eslint-disable-next-line react/no-children-prop
                        children={function (field) {
                          return (
                            <form.Subscribe
                              selector={function (state) {
                                return {
                                  isTopicInvalid:
                                    state.values.topic == null ||
                                    state.values.topic.trim().length < 3,
                                };
                              }}
                              // eslint-disable-next-line react/no-children-prop
                              children={function (subscribeState) {
                                return (
                                  <TextField
                                    data-onboarding-id="modal-generation-keyword"
                                    name={field.name}
                                    sizeField="md"
                                    label={
                                      <div className="flex items-center gap-1">
                                        {t('seoKeyword')}
                                        <GoogleIcon useOriginalColors={true} size={12} />
                                      </div>
                                    }
                                    placeholder={t('keywordRankOnGoogle')}
                                    value={field.state.value}
                                    onChange={function (e) {
                                      field.handleChange(e.target.value);
                                      // Close suggestions when user types manually
                                      if (showKeywordSuggestions) {
                                        setShowKeywordSuggestions(false);
                                      }
                                    }}
                                    onFocus={function () {
                                      if (
                                        shouldShowTooltipsActive &&
                                        shouldShowKeywordTooltip &&
                                        !hasShownKeywordTooltipThisOpenRef.current
                                      ) {
                                        setShowKeywordTooltip(true);
                                        hasShownKeywordTooltipThisOpenRef.current = true;
                                      }
                                    }}
                                    onBlur={function () {
                                      setShowKeywordTooltip(false);
                                    }}
                                    overrideErrorIcon={true}
                                    containerClassName="pr-1"
                                    error={
                                      field.state.meta?.errors.length > 0
                                        ? field.state.meta?.errors
                                        : keywordGenerationError
                                    }
                                    autoComplete="off"
                                    disabled={
                                      keywordGenerationStatus === KeywordGenerationStatus.Generating
                                    }
                                    iconRight={
                                      !subscribeState.isTopicInvalid ? (
                                        <ButtonKeywordGeneration
                                          generationType={keywordGenerationStatus}
                                          generationAction={generatePrimaryKeyword}
                                          isDisabled={subscribeState.isTopicInvalid}
                                        />
                                      ) : null
                                    }
                                  />
                                );
                              }}
                            />
                          );
                        }}
                      />
                    </div>
                    <KeywordSuggestionsDropdown
                      triggerRef={keywordInputFunnelRef}
                      isOpen={showKeywordSuggestions}
                      suggestions={keywordSuggestions}
                      onSelect={function (suggestion: KeywordSuggestion) {
                        form.setFieldValue('keyword', suggestion.primaryKeyword);
                        void form.validateField('keyword', 'change');
                        setShowKeywordSuggestions(false);
                        setKeywordSuggestions([]);
                      }}
                      onClose={function () {
                        setKeywordSuggestions([]);
                        setShowKeywordSuggestions(false);
                      }}
                    />
                  </div>
                  {/* <form.Field
                    name="prompt"
                    validators={{
                      onChange: function (data) {
                        return parseFirstZodError(z.string().min(1, 'PROMPT_REQUIRED'), data.value);
                      },
                    }}
                    // eslint-disable-next-line react/no-children-prop
                    children={function (field) {
                      return (
                        <form.Subscribe
                          selector={function (state) {
                            return {
                              isKeywordInvalid:
                                state.values.keyword == null ||
                                state.values.keyword.trim().length < 3,
                              isTopicInvalid:
                                state.values.topic == null || state.values.topic.trim().length < 3,
                            };
                          }}
                          // eslint-disable-next-line react/no-children-prop
                          children={function (subscribeState) {
                            return (
                              <TextArea
                                name={field.name}
                                size="md"
                                rows={2}
                                label={
                                  <div className="flex items-center gap-1">
                                    {t('prompt')}
                                    <img
                                      src="/llm-logo-generation-prompt.svg"
                                      alt="LLM"
                                      className="h-4"
                                    />
                                  </div>
                                }
                                placeholder={t('promptRankOnGoogle')}
                                value={field.state.value}
                                onChange={function (e) {
                                  field.handleChange(e.target.value);
                                  // Close suggestions when user types manually
                                  if (showKeywordSuggestions) {
                                    setShowKeywordSuggestions(false);
                                  }
                                }}
                                error={
                                  field.state.meta?.errors.length > 0
                                    ? field.state.meta?.errors
                                    : promptGenerationError
                                }
                                description={
                                  <Trans
                                    t={t as unknown as TFunctionNonStrict}
                                    i18nKey="promptHelpText"
                                    components={[
                                      <a
                                        href={getConfig().urlHelpPrompt}
                                        target="_blank"
                                        rel="noreferrer"
                                        key="link"
                                        className="text-branding-accent-500 underline"
                                      >
                                        {t('learnMore')}
                                      </a>,
                                    ]}
                                  />
                                }
                                autoComplete="off"
                                disabled={
                                  promptGenerationStatus === PromptGenerationStatus.Generating
                                }
                                className={
                                  promptGenerationStatus === PromptGenerationStatus.Generating
                                    ? 'bg-branding-gray-50'
                                    : ''
                                }
                                extraElement={
                                  !subscribeState.isKeywordInvalid &&
                                  !subscribeState.isTopicInvalid ? (
                                    <div className="flex w-full justify-end">
                                      <ButtonKeywordGeneration
                                        generationType={promptGenerationStatus}
                                        generationAction={generatePrompt}
                                        isDisabled={
                                          subscribeState.isKeywordInvalid &&
                                          subscribeState.isTopicInvalid
                                        }
                                        className="relative right-[-14px] top-[6px] w-fit"
                                        isPrompt
                                      />
                                    </div>
                                  ) : null
                                }
                              />
                            );
                          }}
                        />
                      );
                    }}
                  /> */}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <form.Field
                      name="keyword"
                      validators={{
                        onChange: function (data) {
                          return parseFirstZodError(
                            z.string().min(1, 'KEYWORD_REQUIRED'),
                            data.value
                          );
                        },
                      }}
                      // eslint-disable-next-line react/no-children-prop
                      children={function (field) {
                        return (
                          <form.Subscribe
                            selector={function (state) {
                              return {
                                isTopicInvalid:
                                  state.values.topic == null ||
                                  state.values.topic.trim().length < 3,
                              };
                            }}
                            // eslint-disable-next-line react/no-children-prop
                            children={function (subscribeState) {
                              return (
                                <TextField
                                  ref={keywordInputOneClickRef}
                                  name={field.name}
                                  sizeField="md"
                                  label={t('searchQuery')}
                                  placeholder={t('keywordRankOnGoogle')}
                                  value={field.state.value}
                                  onChange={function (e) {
                                    field.handleChange(e.target.value);
                                    // Close suggestions when user types manually
                                    if (showKeywordSuggestions) {
                                      setShowKeywordSuggestions(false);
                                    }
                                  }}
                                  onFocus={function () {
                                    if (
                                      shouldShowTooltipsActive &&
                                      shouldShowKeywordTooltip &&
                                      !hasShownKeywordTooltipThisOpenRef.current
                                    ) {
                                      setShowKeywordTooltip(true);
                                      hasShownKeywordTooltipThisOpenRef.current = true;
                                    }
                                  }}
                                  onBlur={function () {
                                    setShowKeywordTooltip(false);
                                  }}
                                  overrideErrorIcon={true}
                                  containerClassName="pr-2"
                                  error={
                                    field.state.meta?.errors.length > 0
                                      ? field.state.meta?.errors
                                      : keywordGenerationError
                                  }
                                  autoComplete="off"
                                  disabled={
                                    keywordGenerationStatus === KeywordGenerationStatus.Generating
                                  }
                                  iconRight={
                                    <ButtonKeywordGeneration
                                      generationType={keywordGenerationStatus}
                                      isDisabled={subscribeState.isTopicInvalid}
                                      generationAction={generatePrimaryKeyword}
                                    />
                                  }
                                />
                              );
                            }}
                          />
                        );
                      }}
                    />
                    <KeywordSuggestionsDropdown
                      triggerRef={keywordInputOneClickRef}
                      isOpen={showKeywordSuggestions}
                      suggestions={keywordSuggestions}
                      onSelect={function (suggestion: KeywordSuggestion) {
                        form.setFieldValue('keyword', suggestion.primaryKeyword);
                        void form.validateField('keyword', 'change');
                        setShowKeywordSuggestions(false);
                        setKeywordSuggestions([]);
                      }}
                      onClose={function () {
                        setKeywordSuggestions([]);
                        setShowKeywordSuggestions(false);
                      }}
                    />
                  </div>
                  <Divider className="mt-2 w-full border-b border-b-branding-gray" />
                </>
              )}

              <div className="flex flex-col gap-2">
                <form.Field
                  name="generationType"
                  validators={{
                    onChange: function (data) {
                      return parseFirstZodError(
                        z.enum([GenerationType.OneClick, GenerationType.Funnel]),
                        data.value
                      );
                    },
                  }}
                  // eslint-disable-next-line react/no-children-prop
                  children={function (field) {
                    return (
                      <>
                        <p className="font-body-2-r mb-2 mt-2">{t('generationType')}</p>
                        <div className="flex flex-col gap-4 md:flex-row">
                          <BlogGenerationCard
                            title={t('oneClickGenerationTitle')}
                            subtitle={t('oneClickGenerationSubtitle')}
                            checked={field.state.value === GenerationType.OneClick}
                            icon={<Rocket02 width={20} height={20} className="font-bold" />}
                            onCheckedChange={function (checked) {
                              field.handleChange(checked ? GenerationType.OneClick : null);
                            }}
                          />
                          <Tooltip text={t('funnelDisabledTooltip')} disabled={!isMobileDevice}>
                            <BlogGenerationCard
                              title={t('funnelGenerationTitle')}
                              subtitle={t('funnelGenerationSubtitle')}
                              icon={<LayersThree02 width={20} height={20} className="font-bold" />}
                              checked={field.state.value === GenerationType.Funnel}
                              disabled={isMobileDevice}
                              onCheckedChange={function (checked) {
                                field.handleChange(checked ? GenerationType.Funnel : null);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </>
                    );
                  }}
                />
              </div>
            </div>
          </DialogBody>
        </form>
        <DialogFooter
          className="relative z-10 mt-0 bg-white px-6 pt-6 transition-shadow duration-200"
          style={
            showFooterShadow
              ? {
                  boxShadow:
                    '0 -20px 24px -4px rgba(10, 13, 18, 0.08), 0 -8px 8px -4px rgba(10, 13, 18, 0.03)',
                }
              : undefined
          }
        >
          <form.Subscribe
            selector={function (state) {
              const canSubmit = isFieldValid(
                state.fieldMeta.generationType,
                state.values.generationType,
                true
              );
              return {
                canSubmit,
                generationType: state.values.generationType,
              };
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (data) {
              return (
                <Button
                  loading={isLoading}
                  size="xl"
                  variant="accent"
                  className="w-full"
                  type="submit"
                  disabled={!data.canSubmit}
                  onClick={function () {
                    form.handleSubmit().catch(function (err) {
                      captureException(
                        err,
                        {
                          file: 'app/blogs/-section-modal-generation.tsx',
                          method: 'onSubmit',
                          infoMessage: 'unable to create content',
                        },
                        trackError
                      );
                    });
                  }}
                >
                  {data.generationType === GenerationType.OneClick
                    ? t('generate', { creditCost: getConfig().creditPerContentCreation })
                    : t('continue', { creditCost: getConfig().creditPerContentCreation })}
                </Button>
              );
            }}
          />
        </DialogFooter>
      </DialogContent>

      {/* Tooltips */}
      {shouldShowTooltipsActive ? (
        <>
          <LightTooltip
            targetId="modal-generation-topic"
            title={t('tooltipTopicTitle')}
            description={t('tooltipTopicDescription')}
            isOpen={showTopicTooltip}
            onClose={function () {
              setShowTopicTooltip(false);
              dismissTooltip(TooltipId.MODAL_GENERATION_TOPIC);
            }}
            side="right"
            align="center"
            className="animate-fade-in-blur"
          />
          <LightTooltip
            targetId="modal-generation-keyword"
            title={t('tooltipKeywordTitle')}
            description={t('tooltipKeywordDescription')}
            isOpen={showKeywordTooltip}
            onClose={function () {
              setShowKeywordTooltip(false);
              dismissTooltip(TooltipId.MODAL_GENERATION_KEYWORD);
            }}
            side="bottom"
            align="center"
            className="animate-fade-in-blur"
          />
        </>
      ) : null}
    </Dialog>
  );
}
